// src/stores/WidgetStore.js - Simplified Zustand Store for Pattern B
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useWidgetStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // SIMPLIFIED STATE (Pattern B)
    // ================================
    
    // Main widget data - single source of truth
    widgets: [],
    widgetsLoading: false,

    // Selected widget state (for widget details/editing)
    selectedWidget: null,
    selectedWidgetLoading: false,

    // Simple operation states
    creatingWidget: false,
    savingWidget: false,
    publishingWidget: false,
    deletingWidget: false,
    
    // Embed code state
    embedCodes: {}, // Keyed by widget_id
    generatingEmbed: false,
    
    // Filter state (for widget type filtering)
    selectedType: 'all', // 'all', 'booking', 'chat', 'contact'
    
    // Connection state
    isConnected: false,
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (Simplified)
    // ================================
    
    /**
     * Request all widgets for the business - NO RETURN VALUE
     * @param {string} widgetType - Optional filter by widget type
     * @param {object} options - Additional options (force, etc.)
     */
    loadWidgets: (widgetType = null, options = {}) => {
      const { widgetsLoading } = get();
      
      if (widgetsLoading && !options.force) {
        console.log('WidgetStore: Already loading widgets, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      console.log('WidgetStore: Loading widgets:', widgetType || 'all types');
      
      set({
        widgetsLoading: true
      });
      
      const requestData = {};
      if (widgetType) {
        requestData.widget_type = widgetType;
      }
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WIDGETS, requestData);
    },

    /**
     * Request detailed information for a specific widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    loadWidgetDetails: (widgetId) => {
      const { selectedWidgetLoading } = get();
      
      if (selectedWidgetLoading) {
        console.log('WidgetStore: Already loading widget details, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for loadWidgetDetails');
        return;
      }

      console.log('WidgetStore: Loading widget details:', widgetId);
      
      set({
        selectedWidgetLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WIDGET_DETAILS, {
        widget_id: widgetId
      });
    },

    /**
     * Create a new widget (starts as inactive) - NO RETURN VALUE
     * @param {object} widgetData - Widget configuration
     */
    createWidget: (widgetData) => {
      const { creatingWidget } = get();
      
      if (creatingWidget) {
        console.log('WidgetStore: Already creating widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetData.widget_type || !widgetData.widget_name) {
        console.error('Widget type and name are required for createWidget');
        return;
      }

      console.log('WidgetStore: Creating widget:', widgetData.widget_name, `(${widgetData.widget_type})`);
      
      set({
        creatingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_WIDGET, widgetData);
    },

    /**
     * Update an existing widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     * @param {object} updateData - Fields to update
     */
    updateWidget: (widgetId, updateData) => {
      const { savingWidget } = get();
      
      if (savingWidget) {
        console.log('WidgetStore: Already saving widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for updateWidget');
        return;
      }

      console.log('WidgetStore: Updating widget:', widgetId);
      
      set({
        savingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_WIDGET, {
        widget_id: widgetId,
        updates: updateData
      });
    },

    /**
     * Publish widget (copy draft config to published config) - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    publishWidget: (widgetId) => {
      const { publishingWidget } = get();

      if (publishingWidget) {
        console.log('WidgetStore: Already publishing widget, skipping');
        return;
      }

      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }

      if (!widgetId) {
        console.error('Widget ID is required for publishWidget');
        return;
      }

      console.log('WidgetStore: Publishing widget:', widgetId);

      set({
        publishingWidget: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.PUBLISH_WIDGET, {
        widget_id: widgetId
      });
    },

    /**
     * Toggle widget active status - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     * @param {boolean} isActive - New active status
     */
    toggleWidgetStatus: (widgetId, isActive) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId || isActive === undefined) {
        console.error('Widget ID and active status are required for toggleWidgetStatus');
        return;
      }

      console.log('WidgetStore: Toggling widget status:', widgetId, isActive ? 'activate' : 'deactivate');

      socketManager.universalHandler.emit(SOCKET_EVENTS.TOGGLE_WIDGET_STATUS, {
        widget_id: widgetId,
        is_active: isActive
      });
    },

    /**
     * Delete a widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    deleteWidget: (widgetId) => {
      const { deletingWidget } = get();
      
      if (deletingWidget) {
        console.log('WidgetStore: Already deleting widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for deleteWidget');
        return;
      }

      console.log('WidgetStore: Deleting widget:', widgetId);

      set({
        deletingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_WIDGET, {
        widget_id: widgetId
      });
    },

    /**
     * Generate embed code for a widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    generateEmbedCode: (widgetId) => {
      const { generatingEmbed, embedCodes } = get();
      
      if (generatingEmbed) {
        console.log('WidgetStore: Already generating embed code, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for generateEmbedCode');
        return;
      }

      // Check if we already have it cached
      const cached = embedCodes[widgetId];
      if (cached) {
        console.log('WidgetStore: Using cached embed code for widget:', widgetId);
        return;
      }

      console.log('WidgetStore: Generating embed code:', widgetId);

      set({
        generatingEmbed: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EMBED_CODE, {
        widget_id: widgetId
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (Simplified)
    // ================================
    
    clearSelectedWidget: () => {
      set({
        selectedWidget: null,
        selectedWidgetLoading: false
      });
    },
    
    refreshWidgets: () => {
      const { selectedType } = get();
      console.log('WidgetStore: Refreshing widgets');
      
      // Force reload
      get().loadWidgets(selectedType === 'all' ? null : selectedType, { force: true });
    },
    
    getWidgetById: (widgetId) => {
      const { widgets } = get();
      return widgets.find(w => w.id === widgetId) || null;
    },
    
    getWidgetsByType: (widgetType) => {
      const { widgets } = get();
      return widgets.filter(w => w.widget_type === widgetType);
    },
    
    getEmbedCode: (widgetId) => {
      const { embedCodes } = get();
      return embedCodes[widgetId] || null;
    },
    
    setSelectedType: (type) => {
      set({ selectedType: type });
    },
    
    // ================================
    // COMPUTED VALUES (Simplified)
    // ================================
    
    getFilteredWidgets: () => {
      const { widgets, selectedType } = get();
      return selectedType === 'all' 
        ? widgets 
        : widgets.filter(w => w.widget_type === selectedType);
    },
    
    getWidgetCounts: () => {
      const { widgets } = get();
      return widgets.reduce((acc, widget) => {
        acc[widget.widget_type] = (acc[widget.widget_type] || 0) + 1;
        return acc;
      }, {});
    },
    
    getHasWidgets: () => {
      const { widgets } = get();
      return widgets.length > 0;
    },
    
    getHasActiveWidgets: () => {
      const { widgets } = get();
      return widgets.some(w => w.is_active);
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (Simplified)
    // ================================
    
    _setWidgetsLoaded: (data) => {
      console.log('WidgetStore: Widgets list received');
      
      const widgetsData = Array.isArray(data) ? data : [];
      
      set({
        widgets: widgetsData,
        widgetsLoading: false
      });
    },
    
    _setWidgetDetailsLoaded: (data) => {
      console.log('WidgetStore: Widget details received');
      
      set({
        selectedWidget: data,
        selectedWidgetLoading: false
      });
    },
    
    _addWidget: (widgetData) => {
      console.log('WidgetStore: Widget created');
      
      set(state => ({
        widgets: [...state.widgets, widgetData],
        creatingWidget: false,
        // Set as selected widget
        selectedWidget: widgetData
      }));
    },
    
    _updateWidget: (widgetData) => {
      console.log('WidgetStore: Widget updated');

      set(state => {
        const updatedWidgets = state.widgets.map(widget =>
          widget.id === widgetData.id ? widgetData : widget
        );

        return {
          widgets: updatedWidgets,
          savingWidget: false,
          // Update selected widget if it's the same one
          selectedWidget: state.selectedWidget && state.selectedWidget.id === widgetData.id
            ? widgetData
            : state.selectedWidget
        };
      });
    },

    _publishWidget: (widgetData) => {
      console.log('WidgetStore: Widget published');

      set(state => {
        const updatedWidgets = state.widgets.map(widget =>
          widget.id === widgetData.id ? widgetData : widget
        );

        return {
          widgets: updatedWidgets,
          publishingWidget: false,
          // Update selected widget if it's the same one
          selectedWidget: state.selectedWidget && state.selectedWidget.id === widgetData.id
            ? widgetData
            : state.selectedWidget
        };
      });
    },

    // UPDATED: Enhanced widget deletion handling
    _removeWidget: (widgetId) => {
      console.log('WidgetStore: Widget deleted:', widgetId);
      
      set(state => {
        const isSelectedWidget = state.selectedWidget && state.selectedWidget.id === widgetId;
        
        // Log if we're clearing the selected widget
        if (isSelectedWidget) {
          console.log('WidgetStore: Clearing selected widget (was deleted)');
        }
        
        const updatedWidgets = state.widgets.filter(widget => widget.id !== widgetId);
        const updatedEmbedCodes = { ...state.embedCodes };
        delete updatedEmbedCodes[widgetId];
        
        return {
          widgets: updatedWidgets,
          deletingWidget: false,
          embedCodes: updatedEmbedCodes,
          // IMPORTANT: Clear selected widget if it was deleted
          selectedWidget: isSelectedWidget ? null : state.selectedWidget,
          // Also clear any related selected widget state
          selectedWidgetLoading: isSelectedWidget ? false : state.selectedWidgetLoading
        };
      });
    },
    
    _setEmbedCodeGenerated: (data) => {
      console.log('WidgetStore: Embed code generated');
      
      set(state => ({
        embedCodes: {
          ...state.embedCodes,
          [data.widget_id]: data
        },
        generatingEmbed: false
      }));
    },
    
    // ================================
    // CONNECTION MANAGEMENT
    // ================================
    
    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },
    
    _getSocketManager: () => {
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },
    
    // ================================
    // INITIALIZATION
    // ================================
    
    _initialize: (socketManager) => {
      const state = get();
      
      console.log('WidgetStore: Initializing with socket manager');
      
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false 
      });
      
      // Auto-load widgets if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('WidgetStore: Auto-loading widgets (first time)');
        state.loadWidgets();
      }
    },
    
    _handleAuthReset: () => {
      console.log('WidgetStore: Resetting all state due to auth reset');

      set({
        widgets: [],
        widgetsLoading: false,
        selectedWidget: null,
        selectedWidgetLoading: false,
        creatingWidget: false,
        savingWidget: false,
        publishingWidget: false,
        deletingWidget: false,
        embedCodes: {},
        generatingEmbed: false,
        selectedType: 'all',
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER SETUP (Simplified)
// ================================

export const initializeWidgetStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('WidgetStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for widget events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useWidgetStore.getState();
    
    switch (eventName) {
      // SUCCESS EVENTS
      case SOCKET_EVENTS.WIDGETS_LOADED:
        store._setWidgetsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.WIDGET_DETAILS_LOADED:
        store._setWidgetDetailsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.WIDGET_CREATED:
        const widgetData = responseData.data?.widget || responseData.data || responseData;
        store._addWidget(widgetData);
        break;
        
      case SOCKET_EVENTS.WIDGET_UPDATED:
        store._updateWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_PUBLISHED:
        store._publishWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_STATUS_TOGGLED:
        store._updateWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_DELETED:
        store._removeWidget(responseData.data.widget_id);
        break;
        
      case SOCKET_EVENTS.EMBED_CODE_GENERATED:
        store._setEmbedCodeGenerated(responseData.data);
        break;
        
      // ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_WIDGETS_FAILED:
        set({ widgetsLoading: false });
        break;

      case SOCKET_EVENTS.GET_WIDGET_DETAILS_FAILED:
        set({ selectedWidgetLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_WIDGET_FAILED:
        set({ creatingWidget: false });
        break;

      case SOCKET_EVENTS.UPDATE_WIDGET_FAILED:
        set({ savingWidget: false });
        break;

      case SOCKET_EVENTS.PUBLISH_WIDGET_FAILED:
        set({ publishingWidget: false });
        break;

      case SOCKET_EVENTS.TOGGLE_WIDGET_STATUS_FAILED:
      case SOCKET_EVENTS.DELETE_WIDGET_FAILED:
      case SOCKET_EVENTS.GET_EMBED_CODE_FAILED:
        console.log('❌ WidgetStore: Operation failed -', responseData.message);
        set({ deletingWidget: false, generatingEmbed: false });
        break;
        
      default:
        // Not a widget-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners
  const widgetEvents = [
    SOCKET_EVENTS.WIDGETS_LOADED,
    SOCKET_EVENTS.WIDGET_DETAILS_LOADED,
    SOCKET_EVENTS.WIDGET_CREATED,
    SOCKET_EVENTS.WIDGET_UPDATED,
    SOCKET_EVENTS.WIDGET_PUBLISHED,
    SOCKET_EVENTS.WIDGET_STATUS_TOGGLED,
    SOCKET_EVENTS.WIDGET_DELETED,
    SOCKET_EVENTS.EMBED_CODE_GENERATED,
    // Error events
    SOCKET_EVENTS.GET_WIDGETS_FAILED,
    SOCKET_EVENTS.GET_WIDGET_DETAILS_FAILED,
    SOCKET_EVENTS.CREATE_WIDGET_FAILED,
    SOCKET_EVENTS.UPDATE_WIDGET_FAILED,
    SOCKET_EVENTS.PUBLISH_WIDGET_FAILED,
    SOCKET_EVENTS.TOGGLE_WIDGET_STATUS_FAILED,
    SOCKET_EVENTS.DELETE_WIDGET_FAILED,
    SOCKET_EVENTS.GET_EMBED_CODE_FAILED
  ];
  
  widgetEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useWidgetStore.getState()._handleAuthReset();
    });
  }
  
  // Initialize the store
  useWidgetStore.getState()._initialize(socketManager);
  
  console.log('WidgetStore: Socket event listeners initialized');
};

export default useWidgetStore;